import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeFilter } from '../../../components/filters/DateRangeFilter';
import { FilterBar } from '../../../components/filters/FilterBar';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { PRIORITY_OPTIONS, STATUS_OPTIONS, type AppealFilters } from '../_lib/appealFilters';

/**
 * Filter toolbar for the appeals moderation list (AUT-130, architect 🟠
 * #6) — status multi-select (LIVE 3-state) + priority multi-select +
 * date range. Extracted from the page so the page stays under the line
 * cap. Every value is URL-state-driven via the parent's `setFilters` patch.
 *
 * NO reason facet (the api doesn't filter on reason) and NO free-text
 * search (the appeals list endpoint has no `q` param). The priority
 * multi-select reuses the generic `StatusMultiSelect` primitive (it's a
 * label-keyed multi-select; "status" in the name is incidental).
 */
export interface AppealsFilterBarProps {
  filters: AppealFilters;
  setFilters: (
    patch: Partial<Record<keyof AppealFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const AppealsFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: AppealsFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('appeals.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <StatusMultiSelect
        status={filters.priority ?? []}
        options={PRIORITY_OPTIONS}
        label={t('appeals.filters.priorityLabel')}
        onChange={(next) => setFilters({ priority: next })}
      />
      <DateRangeFilter
        from={filters.dateFrom ?? null}
        to={filters.dateTo ?? null}
        onChange={(range) => setFilters({ dateFrom: range.from, dateTo: range.to })}
      />
    </FilterBar>
  );
};
